import { execFile } from 'node:child_process';
import type { AgentTool } from './tools';
import { forComponent } from './logging';

const log = forComponent('notify');

/**
 * PushNotification — node-only, OPT-IN OS desktop notification (CC parity: alert the user
 * out-of-band when a long task finishes or needs attention). Kept out of `defaultTools()` and
 * the edge-safe index (same policy as tools.shell.ts); the CLI mounts it explicitly.
 *
 * macOS: osascript `display notification` (no extra deps; terminal-notifier not required).
 * Linux: notify-send (libnotify). Other platforms / missing tools → tool reports unavailability
 * to the model instead of throwing (the task itself should not fail because a toast couldn't show).
 */
/** Windows: a WinRT toast from Windows PowerShell, posted under PowerShell's own AppUserModelID (an
 *  unregistered id is silently dropped by the notification platform). */
const WIN_TOAST = [
  '[Windows.UI.Notifications.ToastNotificationManager, Windows.UI.Notifications, ContentType = WindowsRuntime] | Out-Null',
  '$t = [Windows.UI.Notifications.ToastNotificationManager]::GetTemplateContent([Windows.UI.Notifications.ToastTemplateType]::ToastText02)',
  '$n = $t.GetElementsByTagName("text"); $n.Item(0).AppendChild($t.CreateTextNode($env:AGENTX_NOTIFY_TITLE)) | Out-Null; $n.Item(1).AppendChild($t.CreateTextNode($env:AGENTX_NOTIFY_BODY)) | Out-Null',
  '[Windows.UI.Notifications.ToastNotificationManager]::CreateToastNotifier("{1AC14E77-02E7-4E5D-B744-2EB1AE5198B7}\\WindowsPowerShell\\v1.0\\powershell.exe").Show([Windows.UI.Notifications.ToastNotification]::new($t))',
].join('; ');

export function makeNotifyTool(opts: { platform?: NodeJS.Platform; exec?: typeof execFile } = {}): AgentTool {
  const platform = opts.platform ?? process.platform;
  const run = opts.exec ?? execFile;
  return {
    name: 'PushNotification',
    description:
      'Show an OS desktop notification to the user (out-of-band alert — e.g. a long task finished, input is needed). ' +
      'Use sparingly: only for events the user would want to be interrupted for.',
    parameters: {
      type: 'object',
      required: ['message'],
      properties: {
        message: { type: 'string', description: 'notification body' },
        title: { type: 'string', description: 'notification title (default "agentx")' },
      },
    },
    async run({ message, title }) {
      const msg = String(message ?? '').slice(0, 256);
      const head = String(title ?? 'agentx').slice(0, 64);
      if (!msg) return 'Error: empty message';
      const argv: [string, string[]] | null =
        platform === 'darwin'
          ? ['osascript', ['-e', `display notification ${JSON.stringify(msg)} with title ${JSON.stringify(head)}`]]
          : platform === 'linux'
            ? ['notify-send', [head, msg]]
            : platform === 'win32'
              ? ['powershell.exe', ['-NoProfile', '-NonInteractive', '-Command', WIN_TOAST]]
              : null;
      if (!argv) return `Notifications unavailable on ${platform}.`;
      // Windows: title/body reach the script through env, never interpolated into it.
      const extra = platform === 'win32' ? { windowsHide: true, env: { ...process.env, AGENTX_NOTIFY_TITLE: head, AGENTX_NOTIFY_BODY: msg } } : {};
      return new Promise<string>((resolve) => {
        run(argv[0], argv[1], { timeout: 5000, ...extra }, (e) => {
          if (e) { log.debug('notification failed', e); resolve(`Notification failed: ${e.message}`); }
          else resolve('Notification shown.');
        });
      });
    },
  };
}
